import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SignOut, Trash, Check } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [quotes, setQuotes] = useState([]);

  const loadQuotes = useCallback(async () => {
    const res = await fetch(`${API}/quotes`, { credentials: "include" });
    if (res.ok) setQuotes(await res.json());
  }, []);

  useEffect(() => {
    fetch(`${API}/auth/me`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((u) => { setUser(u); if (u) loadQuotes(); })
      .finally(() => setChecking(false));
  }, [loadQuotes]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    const res = await fetch(`${API}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ email, password }),
    });
    if (res.ok) {
      setUser(await res.json());
      loadQuotes();
    } else {
      const data = await res.json().catch(() => ({}));
      setError(typeof data.detail === "string" ? data.detail : "Accesso non riuscito");
    }
  };

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    setUser(null);
    setQuotes([]);
  };

  const markRead = async (id) => {
    await fetch(`${API}/quotes/${id}/read`, { method: "PATCH", credentials: "include" });
    setQuotes((q) => q.map((x) => (x.id === id ? { ...x, status: "letta" } : x)));
  };

  const remove = async (id) => {
    await fetch(`${API}/quotes/${id}`, { method: "DELETE", credentials: "include" });
    setQuotes((q) => q.filter((x) => x.id !== id));
    toast.success("Richiesta eliminata");
  };

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center text-zinc-500" data-testid="admin-loading">Caricamento…</div>;
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-zinc-50">
        <form onSubmit={login} className="w-full max-w-sm rounded-2xl border border-zinc-200 bg-white p-8 shadow-xl shadow-zinc-200/60" data-testid="admin-login-form">
          <div className="flex items-center gap-3 mb-1">
            <img src="/images/logo.png" alt="I&G Grafiche" className="h-12 w-auto" />
            <span className="text-lg text-zinc-500 font-normal">Admin</span>
          </div>
          <p className="text-sm text-zinc-500 mb-8">Accedi per vedere le richieste di preventivo.</p>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="a-email" className="text-zinc-500 text-xs uppercase tracking-wider">Email</Label>
              <Input id="a-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="bg-white border-zinc-300 text-zinc-900 h-12 rounded-xl" data-testid="admin-input-email" />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="a-pass" className="text-zinc-500 text-xs uppercase tracking-wider">Password</Label>
              <Input id="a-pass" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="bg-white border-zinc-300 text-zinc-900 h-12 rounded-xl" data-testid="admin-input-password" />
            </div>
            {error && <p className="text-sm text-red-500" data-testid="admin-login-error">{error}</p>}
            <button type="submit" data-testid="admin-login-button"
              className="mt-2 px-6 py-3.5 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#FF007F] text-black font-semibold text-sm hover:scale-[1.02] transition-transform">
              Accedi
            </button>
            <a href="/" className="text-center text-xs text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="admin-back-home">← Torna al sito</a>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-6 lg:px-10 py-12 max-w-7xl mx-auto" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
        <div>
          <h1 className="font-display font-semibold text-3xl tracking-tight text-zinc-900">
            Richieste di <span className="text-gradient">Preventivo</span>
          </h1>
          <p className="text-sm text-zinc-500 mt-1" data-testid="admin-quote-count">{quotes.length} richieste totali · {quotes.filter((q) => q.status === "nuova").length} da leggere</p>
        </div>
        <div className="flex gap-3">
          <a href="/" className="px-5 py-2.5 rounded-full border border-zinc-300 text-sm text-zinc-700 hover:border-zinc-500 transition-colors" data-testid="admin-view-site">Vedi il sito</a>
          <button onClick={logout} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-zinc-300 text-sm text-zinc-700 hover:border-[#FF007F] hover:text-[#FF007F] transition-colors" data-testid="admin-logout-button">
            <SignOut size={16} /> Esci
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-zinc-200 bg-white overflow-x-auto shadow-sm">
        <Table data-testid="admin-quotes-table">
          <TableHeader>
            <TableRow className="border-zinc-200 hover:bg-transparent">
              <TableHead className="text-zinc-500">Data</TableHead>
              <TableHead className="text-zinc-500">Nome</TableHead>
              <TableHead className="text-zinc-500">Azienda</TableHead>
              <TableHead className="text-zinc-500">Contatti</TableHead>
              <TableHead className="text-zinc-500">Servizio</TableHead>
              <TableHead className="text-zinc-500">Messaggio</TableHead>
              <TableHead className="text-zinc-500">Stato</TableHead>
              <TableHead className="text-zinc-500 text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {quotes.length === 0 && (
              <TableRow className="border-zinc-100">
                <TableCell colSpan={8} className="text-center text-zinc-500 py-12" data-testid="admin-empty-state">
                  Nessuna richiesta per ora.
                </TableCell>
              </TableRow>
            )}
            {quotes.map((q) => (
              <TableRow key={q.id} className="border-zinc-100 hover:bg-zinc-50" data-testid={`admin-quote-row-${q.id}`}>
                <TableCell className="text-zinc-500 whitespace-nowrap">{fmt(q.created_at)}</TableCell>
                <TableCell className="text-zinc-900 font-medium whitespace-nowrap">{q.name}</TableCell>
                <TableCell className="text-zinc-500">{q.company || "—"}</TableCell>
                <TableCell className="text-zinc-500">
                  <div className="text-xs">{q.email}</div>
                  {q.phone && <div className="text-xs text-zinc-400">{q.phone}</div>}
                </TableCell>
                <TableCell className="text-zinc-700 whitespace-nowrap">{q.service}</TableCell>
                <TableCell className="text-zinc-500 max-w-[280px] truncate" title={q.message}>{q.message}</TableCell>
                <TableCell>
                  <span className={`text-xs px-3 py-1 rounded-full ${q.status === "nuova" ? "bg-cyan-100 text-cyan-700" : "bg-zinc-100 text-zinc-500"}`}
                    data-testid={`admin-quote-status-${q.id}`}>
                    {q.status}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    {q.status === "nuova" && (
                      <button onClick={() => markRead(q.id)} aria-label="Segna come letta" data-testid={`admin-mark-read-${q.id}`}
                        className="p-2 rounded-lg border border-zinc-200 text-zinc-500 hover:text-[#00A5C8] hover:border-[#00A5C8] transition-colors">
                        <Check size={15} />
                      </button>
                    )}
                    <button onClick={() => remove(q.id)} aria-label="Elimina" data-testid={`admin-delete-${q.id}`}
                      className="p-2 rounded-lg border border-zinc-200 text-zinc-500 hover:text-[#FF007F] hover:border-[#FF007F] transition-colors">
                      <Trash size={15} />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
